import { useContext, useEffect, useState } from 'react'
import { ProductContext } from 'src/components/restructure/product/contexts'
import { useWishlistContext } from 'src/components/contexts/wishlist-context'
import useWishlist from 'src/hooks/useWishlist'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import type { Product } from 'src/components/restructure/product/types/product'
import WishlistFilledIcon from 'src/components/Icons/WhistlistSelectIcon'
import WishlistEmtpyIcon from 'src/components/Icons/WhistlistIcon'
import { useMobile } from 'src/hooks/useMobile'
import { useAccountContext } from 'src/components/account/context'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { setCookie } from 'src/utils/cookies'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import NewWishlistEmtpyIcon from 'src/components/Icons/NewWishlistEmtpyIcon'
import NewWishlistExperimentIcon from 'src/components/Icons/NewWishlistExperimentIcon'

import { loginUrl } from '../../../../../../../store.config'

interface WishlistButtonProps {
  product?: Product
  desktopSize: string
  mobileSize: string
  variant?: 'pdp' | 'card' | 'newProductCard'
  isFirstFoldRevampEnabled?: boolean
}

export function WishListButton({
  product: productProps,
  desktopSize,
  mobileSize,
  variant,
  isFirstFoldRevampEnabled = false,
}: WishlistButtonProps) {
  const { product: productData, currentSku } = useContext(ProductContext)
  const [isInWishlist, setIsInWishlist] = useState(false)
  const { isLoggedIn } = useAccountContext()
  const { userWishlist, setUserWishlist, setWishlistId } = useWishlistContext()
  const { isMobile } = useMobile()
  const { pageData } = useGlobalContext()

  const product = productProps ?? productData

  const { getWishlist, addProductToWishlist, removeProductFromWishlist } =
    useWishlist()

  const refreshWishlist = () => {
    getWishlist()
      .then((wishlistData) => {
        setUserWishlist(wishlistData?.data?.products ?? [])
        setWishlistId(wishlistData?.data?.id)

        return wishlistData?.data?.products
      })
      .catch(() => null)
  }

  const sendAmplitudeEvent = () => {
    const productObject = getProductQueryObject(currentSku)

    const standardObject = getStandardObject(pageData)

    if (!standardObject || !productObject) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Product Saved To Wishlist',
      eventData: {
        ...standardObject,
        'product details': [productObject],
      },
    })
  }

  const handleButtonClick = async () => {
    const currentPageUrl = window?.location?.pathname ?? ''

    if (!isLoggedIn) {
      setCookie('loginURL', window.location.href)

      window.location.href = `${loginUrl}?returnUrl=https://www.decathlon.com.br${currentPageUrl}#loginSuccess`

      return
    }

    if (isInWishlist === true) {
      removeProductFromWishlist(product?.productId)
        .then(refreshWishlist)
        .catch(() => setIsInWishlist(true))
      setIsInWishlist(false)

      return
    }

    addProductToWishlist(product?.productId)
      .then(refreshWishlist)
      .catch(() => setIsInWishlist(false))
    setIsInWishlist(true)
    sendAmplitudeEvent()
  }

  useEffect(() => {
    const isInList =
      userWishlist?.some(
        ({ productId }: any) => productId === product?.productId
      ) ?? false

    setIsInWishlist(isInList)
  }, [userWishlist, product?.productId])

  const wishListIcon = isInWishlist ? (
    <WishlistFilledIcon
      aria-label="Botão para remover produto da lista de favoritos"
      data-testid="colorful-wish-list"
      width={isMobile ? mobileSize : desktopSize}
      height={isMobile ? mobileSize : desktopSize}
    />
  ) : (
    <WishlistEmtpyIcon
      aria-label="Botão para adicionar produto na lista de favoritos"
      data-testid="wish-list-icon"
      width={isMobile ? mobileSize : desktopSize}
      height={isMobile ? mobileSize : desktopSize}
    />
  )

  const wishListExperimentIcon = (
    <NewWishlistExperimentIcon
      width={isMobile ? mobileSize : desktopSize}
      height={isMobile ? mobileSize : desktopSize}
      color={isInWishlist ? '#D81124' : 'black'}
      aria-label="Botão para adicionar produto na lista de favoritos"
      data-testid="wish-list-icon"
    />
  )

  const wishListIconNewProductCard = (
    <NewWishlistEmtpyIcon
      aria-label="Botão para adicionar produto na lista de favoritos"
      data-testid="wish-list-icon"
      width={isMobile ? mobileSize : desktopSize}
      height={isMobile ? mobileSize : desktopSize}
      color={isInWishlist ? '#D81124' : undefined}
    />
  )

  const shouldUsePdpRevampExperimentIcon =
    variant === 'pdp' && isMobile && isFirstFoldRevampEnabled

  const shouldUseNewProductCardExperimentIcon = variant === 'newProductCard'

  return (
    <button
      className={
        variant === 'pdp'
          ? 'w-[32px] h-[32px] flex items-center justify-center bg-restructure-background-primary rounded-full'
          : ''
      }
      data-testid={variant === 'pdp' ? 'pdp-wishlist-button' : undefined}
      onClick={handleButtonClick}
      aria-label="add or remove item of wishlist"
    >
      {shouldUsePdpRevampExperimentIcon
        ? wishListExperimentIcon
        : shouldUseNewProductCardExperimentIcon
        ? wishListIconNewProductCard
        : wishListIcon}
    </button>
  )
}
